import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * PJPCoin — Private Jet Pilots spinning 3D emblem.
 *
 * Requires two texture files served as static assets:
 *   /pjp/pjp_coin_color.png
 *   /pjp/pjp_coin_normal.png
 *
 * Props:
 *   speed      revolutions per second (default 0.2)
 *   tilt       degrees of forward tilt (default 0)
 *   spinning   set false to freeze rotation (default true)
 *   className  wrapper classes
 *   style      wrapper inline styles
 *
 * The canvas has a transparent background and fills its parent element,
 * so give the parent an explicit height (e.g. 400px).
 */
export default function PJPCoin({
  speed = 0.2,
  tilt = 0,
  spinning = true,
  colorMap = "/pjp/pjp_coin_color.png",
  normalMap = "/pjp/pjp_coin_normal.png",
  className = "",
  style = {},
  testId,
  forceMotion = false,   // ignore prefers-reduced-motion — useful for small decorative coins (header logo)
}) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const prefersReduced = !forceMotion && window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const spin = prefersReduced || !spinning ? 0 : speed;

    // ---------- renderer ----------
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.18;
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);

    const sizeToMount = () => {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      const vFOV = (camera.fov * Math.PI) / 180;
      const d = 0.95 / (0.8 * Math.tan(vFOV / 2) * Math.min(1, camera.aspect));
      camera.position.set(0, 0.04, d);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
    };

    // ---------- soft studio environment ----------
    const envCanvas = document.createElement("canvas");
    envCanvas.width = 512;
    envCanvas.height = 256;
    const ctx = envCanvas.getContext("2d");
    const grad = ctx.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0, "#2a2e34");
    grad.addColorStop(0.45, "#9aa0a8");
    grad.addColorStop(0.5, "#f2f4f7");
    grad.addColorStop(0.55, "#9aa0a8");
    grad.addColorStop(1, "#1c1f24");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 512, 256);
    const blob = (x, y, r, a) => {
      const rg = ctx.createRadialGradient(x, y, 0, x, y, r);
      rg.addColorStop(0, `rgba(255,255,255,${a})`);
      rg.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = rg;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    };
    blob(150, 110, 150, 0.55);
    blob(380, 130, 120, 0.4);
    const envTex = new THREE.CanvasTexture(envCanvas);
    envTex.mapping = THREE.EquirectangularReflectionMapping;
    envTex.colorSpace = THREE.SRGBColorSpace;
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromEquirectangular(envTex).texture;
    envTex.dispose();
    pmrem.dispose();

    // ---------- textures ----------
    const loader = new THREE.TextureLoader();
    const maxAniso = renderer.capabilities.getMaxAnisotropy();
    const loadTex = (url, srgb) => {
      const t = loader.load(url);
      if (srgb) t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = maxAniso;
      return t;
    };
    const colorTex = loadTex(colorMap, true);
    const normalTex = loadTex(normalMap, false);

    // ---------- medallion ----------
    const R = 0.84;
    const T = 0.2;
    const coin = new THREE.Group();
    const edgeMat = new THREE.MeshStandardMaterial({
      color: 0xf3f3f6,
      metalness: 0,
      roughness: 0.3,
      envMapIntensity: 0.55,
    });
    const faceMat = new THREE.MeshStandardMaterial({
      map: colorTex,
      normalMap: normalTex,
      normalScale: new THREE.Vector2(1.4, 1.4),
      metalness: 0,
      roughness: 0.28,
      envMapIntensity: 0.6,
    });
    const geo = new THREE.CylinderGeometry(R, R, T, 256, 1, false);
    const body = new THREE.Mesh(geo, [edgeMat, faceMat, faceMat]);
    body.rotation.x = Math.PI / 2;
    coin.add(body);
    coin.rotation.x = (tilt * Math.PI) / 180;
    scene.add(coin);

    // ---------- lights ----------
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const key = new THREE.DirectionalLight(0xffffff, 0.95);
    key.position.set(-3, 3, 4);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.4);
    fill.position.set(3, -1, 2);
    scene.add(fill);
    const back = new THREE.DirectionalLight(0xffffff, 0.25);
    back.position.set(0, 1, -4);
    scene.add(back);

    sizeToMount();

    // ---------- loop ----------
    const clock = new THREE.Clock();
    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const dt = clock.getDelta();
      if (spin !== 0) coin.rotation.y += spin * Math.PI * 2 * dt;
      renderer.render(scene, camera);
    };
    animate();

    const ro = new ResizeObserver(sizeToMount);
    ro.observe(mount);

    // ---------- cleanup ----------
    return () => {
      cancelAnimationFrame(frameId);
      ro.disconnect();
      geo.dispose();
      edgeMat.dispose();
      faceMat.dispose();
      colorTex.dispose();
      normalTex.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount)
        mount.removeChild(renderer.domElement);
    };
  }, [speed, tilt, spinning, colorMap, normalMap]);

  return (
    <div
      ref={mountRef}
      data-testid={testId || "pjp-coin"}
      className={className}
      style={{ width: "100%", height: "100%", overflow: "hidden", ...style }}
    />
  );
}
